import {
  clampWidgetRadiusPx,
  escapeHtmlAttribute,
  normalizeHex6,
  previewHexForLiveDisplay,
  tryParseCompleteHex,
  widgetApiBaseUrl,
} from '@/components/CampaignWidgetManager/widgetPreviewTokens'
import { env } from '@/lib/env'
import type { LandingCampaign } from '@/types/landing'
import { resolveAttributionPageUrl } from '@/lib/campaign-page-url'

const FALLBACK_BUTTON = '#5e17eb'
const FALLBACK_TEXT = '#ffffff'
const FALLBACK_RADIUS = 8

export interface WidgetEmbedSnippetFields {
  widget_embed_button_text: string
  widget_embed_button_color: string
  widget_embed_button_text_color: string
  widget_embed_button_radius_px: string
}

export function resolveLandingWidgetEmbedFields(campaign: LandingCampaign): WidgetEmbedSnippetFields {
  return {
    widget_embed_button_text: campaign.widget_embed_button_text ?? '',
    widget_embed_button_color: campaign.widget_embed_button_color ?? '',
    widget_embed_button_text_color: campaign.widget_embed_button_text_color ?? '',
    widget_embed_button_radius_px:
      campaign.widget_embed_button_radius_px != null
        ? String(campaign.widget_embed_button_radius_px)
        : '',
  }
}

export function resolveLandingWidgetButtonPresentation(campaign: LandingCampaign): {
  effectiveButtonColor: string
  effectiveTextColor: string
  radiusPx: number
} {
  const landingButtonColor = campaign.button_color ?? ''
  return resolveWidgetSnippetButtonPresentation(
    resolveLandingWidgetEmbedFields(campaign),
    landingButtonColor,
  )
}

/** Cores do widget na página de conta: alinhadas à cor primária da campanha (comunicação e mídia). */
export function resolveLandingAccountWidgetButtonPresentation(campaign: LandingCampaign): {
  effectiveButtonColor: string
  effectiveTextColor: string
  radiusPx: number
} {
  const fields = resolveLandingWidgetEmbedFields(campaign)
  const primaryBrandColor = normalizeHex6(
    campaign.primary_color ?? campaign.button_color ?? '',
    FALLBACK_BUTTON,
  )
  const committedText = tryParseCompleteHex(fields.widget_embed_button_text_color) ?? FALLBACK_TEXT
  return {
    effectiveButtonColor: primaryBrandColor,
    effectiveTextColor: previewHexForLiveDisplay(
      fields.widget_embed_button_text_color,
      committedText,
      FALLBACK_TEXT,
    ),
    radiusPx: clampWidgetRadiusPx(fields.widget_embed_button_radius_px, FALLBACK_RADIUS),
  }
}

export function buildLandingAccountWidgetScriptAttributes(
  campaign: LandingCampaign,
  options: { publicAppUrl: string },
): Record<string, string> {
  const { effectiveButtonColor, effectiveTextColor, radiusPx } =
    resolveLandingAccountWidgetButtonPresentation(campaign)
  const spaOrigin = options.publicAppUrl.replace(/\/+$/, '')
  const apiUrl = (widgetApiBaseUrl() || spaOrigin).replace(/\/+$/, '')
  const pageUrl = resolveAttributionPageUrl()

  const attributes: Record<string, string> = {
    'data-campaign-id': String(campaign.id),
    'data-api-url': apiUrl,
    'data-public-app-url': spaOrigin,
    'data-hide-button': '1',
    'data-button-text': 'Comprar',
    'data-primary-color': effectiveButtonColor,
    'data-button-color': effectiveButtonColor,
    'data-button-text-color': effectiveTextColor,
    'data-button-radius': `${radiusPx}px`,
  }

  if (pageUrl) {
    attributes['data-page-url'] = pageUrl.slice(0, 2000)
  }

  return attributes
}

type WidgetSnippetPresentationOptions = {
  /** Quando true, ignora widget_embed_button_color e usa só a cor de marca passada. */
  ignoreEmbedButtonColor?: boolean
}

function resolveWidgetSnippetButtonPresentation(
  fields: WidgetEmbedSnippetFields,
  landingBrandColor: string,
  options?: WidgetSnippetPresentationOptions,
): { effectiveButtonColor: string; effectiveTextColor: string; radiusPx: number } {
  const landingBtn = normalizeHex6(landingBrandColor, FALLBACK_BUTTON)
  const embedBtn = options?.ignoreEmbedButtonColor
    ? null
    : tryParseCompleteHex(fields.widget_embed_button_color)
  const committedBtn = embedBtn ?? landingBtn
  const committedText = tryParseCompleteHex(fields.widget_embed_button_text_color) ?? FALLBACK_TEXT
  return {
    effectiveButtonColor: options?.ignoreEmbedButtonColor
      ? committedBtn
      : previewHexForLiveDisplay(fields.widget_embed_button_color, committedBtn, FALLBACK_BUTTON),
    effectiveTextColor: previewHexForLiveDisplay(
      fields.widget_embed_button_text_color,
      committedText,
      FALLBACK_TEXT,
    ),
    radiusPx: clampWidgetRadiusPx(fields.widget_embed_button_radius_px, FALLBACK_RADIUS),
  }
}

/**
 * Snippet do widget em modo somente combo (`data-combo-id`), alinhado ao embed da campanha.
 */
export function buildComboOnlyWidgetSnippet(
  campaignId: number,
  comboId: number,
  comboCtaText: string,
  fields: WidgetEmbedSnippetFields,
  landingButtonColor: string,
): string {
  const apiUrl = widgetApiBaseUrl()
  const scriptUrl = `${apiUrl}/js/widget.js?v=${encodeURIComponent(env.widgetAssetVersion)}`
  const { effectiveButtonColor, effectiveTextColor, radiusPx } = resolveWidgetSnippetButtonPresentation(
    fields,
    landingButtonColor,
  )
  const label = (comboCtaText.trim() || fields.widget_embed_button_text.trim() || 'Comprar combo').trim()
  const safeText = escapeHtmlAttribute(label)
  const safeApi = escapeHtmlAttribute(apiUrl)
  const safeScript = escapeHtmlAttribute(scriptUrl)
  return `<script 
  src="${safeScript}" 
  data-campaign-id="${campaignId}" 
  data-combo-id="${comboId}"
  data-api-url="${safeApi}"
  data-button-text="${safeText}" 
  data-button-color="${escapeHtmlAttribute(effectiveButtonColor)}" 
  data-button-text-color="${escapeHtmlAttribute(effectiveTextColor)}" 
  data-button-radius="${radiusPx}px">
</script>`
}
